import { useEffect, useMemo, useState } from 'react'
import { Sheet } from 'react-modal-sheet'
import BottomSheet from '~components/ui/bottom-sheet'
import Icon from '~components/ui/icon'
import useBreakPoint from '~hooks/use-break-point'
import Consultation from '~models/consultation'
import { cn } from '~utils/cn'

type ListItemProps = Readonly<{
  title: string
  value: string
  type?: 'row' | 'col'
  mode?: 'desktop' | 'mobile'
}>

const ListItem = ({ title, value, type = 'row', mode = 'desktop' }: ListItemProps) => {
  return (
    <div
      className={cn('flex border-b border-solid border-gray-200 p-1 last:border-b-0', {
        'flex-col flex-gap-y-1': type === 'col',
        'items-center justify-between': type === 'row',
      })}
    >
      <div className={cn('text-label-w-1 text-gray-500', mode === 'mobile' && 'text-body-2')}>
        {title}
      </div>
      <div
        className={cn(
          'text-gray-900 text-label-3',
          mode === 'mobile' && 'text-body-1 dark:text-gray-100'
        )}
      >
        {value}
      </div>
    </div>
  )
}

type IssueDetailsProps = Readonly<{
  checkupData: [string, string][]
  mode?: 'desktop' | 'mobile'
}>

export const IssueDetails = ({ checkupData, mode = 'desktop' }: IssueDetailsProps) => {
  return (
    <>
      {checkupData.map(([title, value]) => (
        <ListItem key={title} title={title} value={value} mode={mode} type="col" />
      ))}
    </>
  )
}

type IssueSummaryCardProps = Readonly<{
  title: string
  values: string[]
  index: number
}>

const IssueSummaryCard = ({ title, values, index }: IssueSummaryCardProps) => {
  return (
    <div className="rounded-lg bg-white px-3 py-2 dark:bg-gray-900">
      <div className="text-gray-700 dark:text-gray-500 text-body-3">{title}</div>
      <div className="mt-4 flex">
        {values.map((value, i) => (
          <div
            key={value}
            className={cn(
              'text-gray-800 text-label-2 dark:text-gray-400',
              index === 0 && i === 0 && 'text-red-500 dark:text-secondary-500' // Main concern만 red highlight 처리
            )}
          >
            {value}
            {values.length - 1 !== i && `,\u00A0`}
          </div>
        ))}
      </div>
    </div>
  )
}

const getSortedCheckupSummary = (checkupSummary: Consultation.CheckupSummary) => {
  const order = ['Main concern', 'Duration', 'Other issues']

  const hasKey = (obj: Consultation.CheckupSummary, key: string) =>
    Object.prototype.hasOwnProperty.call(obj, key)

  const safeGet = (obj: Consultation.CheckupSummary, key: string, defaultValue = []) => {
    return hasKey(obj, key) ? obj[key] : defaultValue
  }

  const getSortedEntries = (
    obj: Consultation.CheckupSummary,
    order: string[]
  ): [string, string[]][] => {
    return order
      .filter((key: string) => hasKey(obj, key))
      .map((key: string) => [key, safeGet(obj, key)])
  }

  return getSortedEntries(checkupSummary, order)
}

type IssueDetailsSliderProps = Readonly<{
  checkupSummary: Consultation.CheckupSummary
  checkupData?: [string, string][]
}>

export const IssueDetailsSlider = ({ checkupSummary, checkupData }: IssueDetailsSliderProps) => {
  const breakPoint = useBreakPoint()
  const [openSheet, setOpenSheet] = useState(false)

  const checkupSummaryData = useMemo(
    () => getSortedCheckupSummary(checkupSummary),
    [checkupSummary]
  )

  useEffect(() => {
    if (breakPoint.lg) {
      setOpenSheet(false)
    }
  }, [breakPoint.lg, setOpenSheet])

  return (
    <div className="px-5">
      <div className="flex h-10 items-center justify-between">
        <div className="text-gray-900 text-title-1 dark:text-white">Issue details</div>
        {checkupData && checkupData.length > 1 && (
          <button
            type="button"
            className="flex items-center text-gray-500 text-body-3 bg-transparent"
            onClick={() => setOpenSheet(true)}
          >
            See all
            <div className="px-1 py-3">
              <Icon name="chevron_right" className="size-4 fill-gray-500" />
            </div>
          </button>
        )}
      </div>
      <div
        className={cn(
          'no-scrollbar -mx-5 mt-2 flex overflow-x-scroll',
          checkupSummaryData?.length && 'flex-gap-x-2'
        )}
      >
        {checkupSummaryData?.length ? (
          // Ask a vet 리뉴얼 후 checkup_data가 있는 질문들
          checkupSummaryData.map((summary, index) => {
            const hasValues = summary[1].length > 0

            if (!hasValues) {
              return null
            }

            return (
              <div key={index} className="shrink-0 last:mr-5">
                <IssueSummaryCard title={summary[0]} values={summary[1]} index={index} />
              </div>
            )
          })
        ) : checkupData?.length ? (
          // Ask a vet 리뉴얼 전 checkup_data가 없는 legacy 질문들
          <div className="shrink-0 first:ml-5 last:mr-5">
            <IssueSummaryCard title={checkupData[0][0]} values={[checkupData[0][1]]} index={0} />
          </div>
        ) : null}
      </div>
      {checkupData && (
        <BottomSheet isOpen={openSheet} onCloseRequest={() => setOpenSheet(false)}>
          <div className="p-5 text-gray-900 text-headline-3 dark:text-white">Issue details</div>
          <Sheet.Scroller>
            <div className="px-5 py-3">
              <IssueDetails checkupData={checkupData} mode="mobile" />
            </div>
          </Sheet.Scroller>
        </BottomSheet>
      )}
    </div>
  )
}
